/**
 * DepartmentsProvider
 * Contain most logic handing with Omnitech Api
 */

import React, { useContext, useMemo, useState, useEffect } from 'react'
import _ from 'lodash'
import { CacheChecksumContext, createPersistedState } from '../../cache'
import { cancelRequest } from '../../helpers'
import DepartmentsContext from './departments-context'
import { fetchDepartments } from './departments-api'

// create use state hook with persisted storage
// TODO: support using different storage provider
const useDepartmentsState = createPersistedState('departments')
const useDepartmentsChecksumState = createPersistedState('departmentsChecksum')

export default function DepartmentsProvider({ children, config, currentLocale }) {
  // handle cache checksum value
  const { getChecksum } = useContext(CacheChecksumContext)
  const cacheChecksumValue = getChecksum('departments')

  // internal state
  const [departments, setDepartments] = useDepartmentsState([])
  const [departmentsChecksum, setDepartmentsChecksum] = useDepartmentsChecksumState('')
  const [departmentsReady, setDepartmentsReady] = useState(false)
  const [enableRefresh, setEnableRefresh] = useState(false)

  // get cache with current locale
  const availableLocales = useMemo(() => (
    _.get(config, 'locale.availableLocales')
  ), [config])
  const currentLocaleData = useMemo(() => (
    _.get(departments, currentLocale, [])
  ), [currentLocale, departments])
  const currentLocaleChecksum = useMemo(() => (
    _.get(departmentsChecksum, currentLocale, '')
  ), [currentLocale, departmentsChecksum])

  function apiFetchDepartments(page = 1) {
    return fetchDepartments({
      includes: [
        // 'category_ids'
        'seo',
      ].join(','),
      page
    })
  }

  /**
   * handleFetchDepartments
   * get department with category ids for caching, will be used for making menu
   */
  async function handleFetchDepartments() {
    // use data in cache first
    if (
      !_.isEmpty(currentLocaleData)
      && currentLocaleChecksum === cacheChecksumValue
    ) {
      setDepartmentsReady(true)
      setEnableRefresh(true)
      return
    }

    try {
      setEnableRefresh(false)
      const { cacheChecksum, departments: data } = await apiFetchDepartments()
      setDepartments({
        ..._.pick(departments, availableLocales),
        [currentLocale]: data,
      })
      setDepartmentsReady(true)
      setEnableRefresh(true)
      setDepartmentsChecksum({
        ..._.pick(departmentsChecksum, availableLocales),
        [currentLocale]: cacheChecksum,
      })
    } catch (error) {
      console.warn('fail to load departments for cache: ', error)
    }
  }

  /**
   * handleRefreshDepartmentsCache
   * refresh departments cache when checksum is changed
   */
  async function handleRefreshDepartmentsCache() {
    // when it is not required to update cache, skip the process
    if (!enableRefresh || currentLocaleChecksum === cacheChecksumValue) {
      if (_.isEmpty(currentLocaleChecksum) && !_.isEmpty(cacheChecksumValue)) {
        setDepartmentsChecksum({
          ..._.pick(departmentsChecksum, availableLocales),
          [currentLocale]: cacheChecksumValue,
        })
      }
      return
    }

    try {
      // start refreshing cache data
      setEnableRefresh(false)
      const { cacheChecksum, departments: data } = await apiFetchDepartments()
      setDepartments({
        ..._.pick(departments),
        [currentLocale]: data,
      })
      setDepartmentsChecksum({
        ..._.pick(departmentsChecksum, availableLocales),
        [currentLocale]: cacheChecksum,
      })
    } catch (error) {
      console.warn('fail to refresh departments for cache', error)
    } finally {
      setEnableRefresh(true)
    }
  }

  /**
   * get all department from omnitech API when app is mounted
   */
  useEffect(() => {
    handleFetchDepartments()
    return function cleanUp() {
      cancelRequest.cancelAll(['fetchDepartments'])
    }
  }, [])

  /**
   * update department cache base on cache checksum
   */
  useEffect(() => {
    if (!departmentsReady || _.isUndefined(cacheChecksumValue)) return
    handleRefreshDepartmentsCache()
    return function cleanUp() {
      cancelRequest.cancelAll(['fetchDepartments'])
    }
  }, [cacheChecksumValue, currentLocaleChecksum, departmentsReady])

  const state = {
    departments: currentLocaleData,
    departmentsReady,
    fetchDepartments,
  }

  return (
    <DepartmentsContext.Provider value={state}>
      {children}
    </DepartmentsContext.Provider>
  )
}
